import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight, Zap, BarChart3, Bot, FileText, Clock, Shield, TrendingUp, CheckCircle2,
  Heart, Scale, GraduationCap, ShoppingCart, Truck, Calculator, ChevronRight
} from "lucide-react";
import { industries } from "@/data/industries";

const iconMap = {
  Heart, Scale, GraduationCap, ShoppingCart, Truck, Calculator
};

const stats = [
  { value: "17+", label: "Industries Served", icon: BarChart3, accent: "card-accent-blue", iconBg: "icon-bg-blue", iconColor: "text-blue-600" },
  { value: "90+", label: "Use Cases Mapped", icon: FileText, accent: "card-accent-teal", iconBg: "icon-bg-teal", iconColor: "text-teal-600" },
  { value: "70%", label: "Avg. Time Saved", icon: Clock, accent: "card-accent-cyan", iconBg: "icon-bg-cyan", iconColor: "text-cyan-600" },
  { value: "100%", label: "Custom Solutions", icon: Shield, accent: "card-accent-violet", iconBg: "icon-bg-violet", iconColor: "text-violet-600" },
];

const capabilities = [
  {
    icon: Bot,
    title: "AI Chatbots & Agents",
    desc: "24/7 customer support, lead qualification, and internal helpdesk agents that understand your business context.",
    accent: "card-accent-blue",
    iconBg: "icon-bg-blue",
    iconColor: "text-blue-600"
  },
  {
    icon: Zap,
    title: "Workflow Automation",
    desc: "Eliminate repetitive manual processes. From document generation to data entry, we automate end-to-end.",
    accent: "card-accent-teal",
    iconBg: "icon-bg-teal",
    iconColor: "text-teal-600"
  },
  {
    icon: BarChart3,
    title: "Predictive Analytics",
    desc: "Turn your data into decisions. Demand forecasting, churn prediction, and intelligent inventory management.",
    accent: "card-accent-cyan",
    iconBg: "icon-bg-cyan",
    iconColor: "text-cyan-600"
  },
  {
    icon: FileText,
    title: "Document Intelligence",
    desc: "Extract, summarize, and generate documents. Contracts, reports, invoices processed automatically.",
    accent: "card-accent-violet",
    iconBg: "icon-bg-violet",
    iconColor: "text-violet-600"
  },
];

export default function HomePage() {
  const featuredIndustries = industries.filter((i) => i.featured).slice(0, 6);

  return (
    <div data-testid="home-page">
      {/* ── Hero Section ── */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="hero-glow absolute inset-0" />
        <div className="hero-glow-accent absolute inset-0" />
        <div className="hero-glow-center absolute inset-0" />
        <div className="dot-grid absolute inset-0 opacity-40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-36 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7 animate-fade-in-up">
              <Badge
                variant="outline"
                className="mb-6 px-4 py-1.5 text-blue-600 border-blue-200 bg-blue-50/80 font-semibold tracking-wide backdrop-blur-sm"
                data-testid="hero-badge"
              >
                <Zap className="w-3 h-3 mr-1.5" strokeWidth={2.5} />
                AI Consulting for 17+ Industries
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.08] mb-6">
                Stop doing manually<br />
                <span className="gradient-text">what AI can automate</span>
              </h1>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl mb-8">
                We identify the workflows bleeding your time and money, then engineer
                AI solutions that eliminate them. From hospitals to factories,
                law firms to logistics.
              </p>
              <div className="flex flex-wrap gap-4 mb-10">
                <Link to="/contact">
                  <Button
                    data-testid="hero-cta-primary"
                    className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-8 py-3 h-auto font-semibold btn-glow transition-all hover:-translate-y-0.5"
                    size="lg"
                  >
                    Book a Free Consultation
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button
                    data-testid="hero-cta-secondary"
                    variant="outline"
                    className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-blue-200 rounded-lg px-8 py-3 h-auto font-medium transition-all"
                    size="lg"
                  >
                    Explore Services
                  </Button>
                </Link>
              </div>
              {/* Floating trust badges */}
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: TrendingUp, label: "70% time saved on avg." },
                  { icon: Shield, label: "Enterprise-grade security" },
                  { icon: Clock, label: "Results in weeks" },
                ].map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white/80 backdrop-blur-sm border border-slate-200/80 rounded-full text-xs text-slate-600 font-medium shadow-sm floating-stat"
                  >
                    <Icon className="w-3.5 h-3.5 text-blue-500" strokeWidth={2} />
                    {label}
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-5 hidden md:block animate-slide-in-right">
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-br from-blue-100/50 to-teal-100/30 rounded-3xl blur-3xl" />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/10 to-teal-500/5" />
                <img
                  src="https://images.unsplash.com/photo-1758691736591-5bf31a5d0dea?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="AI automation workflow visualization"
                  className="relative rounded-2xl shadow-2xl shadow-blue-200/40 w-full object-cover aspect-[4/3] ring-1 ring-blue-100"
                  data-testid="hero-image"
                />
                {/* Floating metric chip */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg shadow-slate-200 border border-slate-100 px-4 py-2.5 flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg icon-bg-teal flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-teal-600" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 leading-none mb-0.5">Avg. efficiency</p>
                    <p className="text-sm font-bold text-slate-900">+70% faster</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats Section ── */}
      <section className="border-y border-slate-100 section-gradient-light" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div
                  key={stat.label}
                  className={`bg-white rounded-xl p-6 text-center border border-slate-100 shadow-card-base card-lift ${stat.accent}`}
                  data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${stat.iconBg} flex items-center justify-center mx-auto mb-3`}>
                    <Icon className={`w-5 h-5 ${stat.iconColor}`} strokeWidth={1.5} />
                  </div>
                  <p className="font-heading text-3xl md:text-4xl font-bold stat-value tracking-tight">
                    {stat.value}
                  </p>
                  <p className="text-xs text-slate-500 mt-1.5 font-medium">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Capabilities Section ── */}
      <section className="py-20 md:py-28" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-600 mb-3">
              What We Do
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              AI solutions engineered for<br />
              <span className="gradient-text">real business problems</span>
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              We don't sell generic AI. Every solution is mapped to a specific pain point
              in your industry, designed to deliver measurable ROI.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {capabilities.map((cap, index) => {
              const Icon = cap.icon;
              return (
                <Card
                  key={cap.title}
                  className={`bg-white border border-slate-200 hover:border-transparent transition-all duration-300 group card-lift ${cap.accent}`}
                  data-testid={`capability-card-${index}`}
                >
                  <CardContent className="p-8">
                    <div className={`w-12 h-12 rounded-xl ${cap.iconBg} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-6 h-6 ${cap.iconColor}`} strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2.5">
                      {cap.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {cap.desc}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Featured Industries ── */}
      <section className="py-20 md:py-28 section-gradient-bottom" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-600 mb-3">
                Industries
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Built for your industry
              </h2>
            </div>
            <Link to="/services">
              <Button
                variant="outline"
                className="border-slate-200 text-slate-700 hover:bg-white hover:border-blue-200 rounded-lg"
                data-testid="view-all-industries-btn"
              >
                View All 17+ Industries
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredIndustries.map((industry) => (
              <Link key={industry.id} to={`/services?industry=${industry.id}`}>
                <Card
                  className="bg-white border border-slate-200 hover:border-blue-200 hover:shadow-card-hover transition-all duration-300 group h-full card-lift"
                  data-testid={`industry-card-${industry.id}`}
                >
                  <CardContent className="p-6">
                    <div className={`w-10 h-10 rounded-xl ${industry.bgColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <span className={`text-lg font-bold ${industry.color}`}>
                        {industry.name.charAt(0)}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1.5 group-hover:text-blue-600 transition-colors">
                      {industry.name}
                    </h3>
                    <p className="text-xs text-slate-400 font-medium mb-3">
                      {industry.segments.length} automation use cases
                    </p>
                    <ul className="space-y-2">
                      {industry.segments.slice(0, 3).map((seg) => (
                        <li key={seg.name} className="flex items-start gap-2 text-xs text-slate-600">
                          <CheckCircle2 className="w-3.5 h-3.5 text-teal-500 mt-0.5 shrink-0" />
                          <span>{seg.solution}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 flex items-center text-xs font-semibold text-blue-600 group-hover:gap-2 transition-all gap-1">
                      See all solutions
                      <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Approach / Process ── */}
      <section className="py-20 md:py-28 bg-white" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-600 mb-3">
              Our Process
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              From pain point to production<br />
              <span className="gradient-text">in weeks</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Diagnose",
                desc: "We audit your workflows, identify bottlenecks, and map the exact manual processes costing you time and money.",
                iconBg: "icon-bg-blue",
                numColor: "text-blue-600",
                accent: "card-accent-blue"
              },
              {
                step: "02",
                title: "Engineer",
                desc: "Our team designs and builds custom AI solutions tailored to your specific use case, industry, and scale.",
                iconBg: "icon-bg-teal",
                numColor: "text-teal-600",
                accent: "card-accent-teal"
              },
              {
                step: "03",
                title: "Deploy & Scale",
                desc: "We deploy, monitor, and iterate. You see measurable results within weeks, not months.",
                iconBg: "icon-bg-cyan",
                numColor: "text-cyan-600",
                accent: "card-accent-cyan"
              }
            ].map((item, idx) => (
              <div
                key={item.step}
                className={`bg-white border border-slate-200 rounded-xl p-8 card-lift ${item.accent}`}
                data-testid={`process-step-${idx}`}
              >
                <div className={`w-12 h-12 rounded-xl ${item.iconBg} flex items-center justify-center mb-5`}>
                  <span className={`font-heading text-lg font-bold ${item.numColor}`}>{item.step}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="py-20 md:py-28 cta-gradient" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            Ready to automate what's slowing you down?
          </h2>
          <p className="text-base text-blue-100 max-w-2xl mx-auto mb-10 leading-relaxed">
            Book a free consultation. We'll identify your biggest automation opportunities
            and show you exactly how AI can solve them.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="cta-book-consultation"
                className="bg-white text-blue-700 hover:bg-blue-50 rounded-lg px-8 py-3 h-auto font-bold shadow-lg hover:-translate-y-0.5 transition-all"
                size="lg"
              >
                Book a Free Consultation
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
            <Link to="/case-studies">
              <Button
                data-testid="cta-view-work"
                variant="outline"
                className="border-white/30 text-white bg-white/10 hover:bg-white/20 rounded-lg px-8 py-3 h-auto font-medium backdrop-blur-sm transition-all"
                size="lg"
              >
                View Our Work
              </Button>
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-blue-200">
            <span className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> 70% avg. efficiency gain
            </span>
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4" /> Enterprise-grade security
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" /> Results in weeks
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
